import { useLayoutEffect, useMemo, useRef } from "react"
import { useAppSelector } from "hooks/redux"
import {
  graphSlice,
  DEFAULT_GRAPH_STATE,
  GRAPH_PREFERENCE_BASE,
  selectGraphPanel,
} from "connected-components/dashboard/store/graph-slice"
import { useDashboardSliceData } from "connected-components/dashboard/hooks/use-dashboard-slice-data"
import { getToday } from "helpers/types/dates"
import { resolveDateOverride, getEffectiveLatestDate, type DateOverride } from "connected-components/dashboard/helpers/date-override"

// Pure slice reader — does NOT sync preferences. GraphWrapper calls useSyncGraphPreferences once;
// calling it here would make every consumer re-sync and crash outside a PreferenceContext (Storybook).
export function useGraphData(initialSelectedTemplate?: string, initialDateOverride?: DateOverride | null) {
  const { dashboardId, panelKey, dispatch, modelId, core, trade } = useDashboardSliceData(GRAPH_PREFERENCE_BASE)

  const graphInstance = useAppSelector((s) => selectGraphPanel(s, dashboardId, panelKey))
  const graphState = graphInstance ?? DEFAULT_GRAPH_STATE
  // Read the raw (un-coalesced) instance so GraphWrapper can hold the template tree until the slice
  // is seeded — otherwise nested consumers paint DEFAULT before the config seed lands.
  const isGraphInitialized = graphInstance != null

  // Layout effect for parity with the other panel seeds. Graph doesn't omit its field from the
  // fallback, so a saved template still overrides this seed; the phase only makes ordering deterministic.
  const seeded = useRef(false)
  useLayoutEffect(() => {
    if (seeded.current) return
    // Wait for a template before latching, or a config arriving on a later render never seeds.
    if (initialSelectedTemplate == null) return
    seeded.current = true
    dispatch(graphSlice.actions.initFromModuleConfig({
      dashboardId,
      panelId: panelKey,
      selectedTemplate: initialSelectedTemplate,
      dateOverride: initialDateOverride,
    }))
  }, [dashboardId, panelKey, dispatch, initialSelectedTemplate, initialDateOverride])

  const graphUpdaters = useMemo(() => ({
    setSelectedTemplate: (template: string) =>
      dispatch(graphSlice.actions.setSelectedTemplate({ dashboardId, panelId: panelKey, template })),
    setDisabledItems: (itemId: string, defaultDisabled?: boolean) =>
      dispatch(graphSlice.actions.setDisabledItems({ dashboardId, panelId: panelKey, itemId, defaultDisabled })),
    setOnlyItem: (itemId: string, allItemIds: string[]) =>
      dispatch(graphSlice.actions.setOnlyItem({ dashboardId, panelId: panelKey, itemId, allItemIds })),
    enableItems: (itemIds: string[]) =>
      dispatch(graphSlice.actions.enableItems({ dashboardId, panelId: panelKey, itemIds })),
    setDateOverride: (dateOverride: DateOverride) =>
      dispatch(graphSlice.actions.setDateOverride({ dashboardId, panelId: panelKey, dateOverride })),
    clearDateOverride: () =>
      dispatch(graphSlice.actions.clearDateOverride({ dashboardId, panelId: panelKey })),
  }), [dispatch, dashboardId, panelKey])

  // Panel-level override supersedes the dashboard's dates when present; templates read
  // startDate/endDate transparently, so effectiveDates is spread last to win over ...core's raw dates.
  // resolveDateOverride re-derives startDate/endDate from the override's period against the panel's
  // *current* earliestDate/latestDate on every read (so a stored "3M" never goes stale, and a shrunk
  // range from an instrument/model switch is picked up) and derives a separate performanceStartDate,
  // since Performance queries with that pair instead of startDate/endDate.
  const effectiveDates = useMemo(() => {
    const effectiveLatestDate = getEffectiveLatestDate(core.isIntraday, core.endDate, core.latestDate)
    const resolved = graphState.dateOverride
      ? resolveDateOverride(graphState.dateOverride, core.earliestDate, effectiveLatestDate)
      : null
    const endDate = resolved?.endDate ?? core.endDate
    return {
      startDate: resolved?.startDate ?? core.startDate,
      endDate,
      performanceStartDate: resolved?.performanceStartDate ?? core.performanceStartDate,
      performanceEndDate: resolved?.endDate ?? core.performanceEndDate,
      // A stored override can still resolve to null (boundaries not loaded yet, or a collapsed
      // fixed-length window) — callers gate "is an override actually in effect" on this, not on
      // the raw dateOverride, since dates fall back to core's in that case.
      isDateOverrideActive: resolved != null,
      // core.isIntraday only means "live" when the panel's own effective endDate (post override)
      // is today — a panel viewing a historical single day (e.g. a collapsed 1D/MTD/YTD override)
      // must not route through the live intraday path just because the dashboard itself is.
      isIntradayMode: core.isIntraday && endDate === getToday(),
    }
  }, [graphState.dateOverride, core.isIntraday, core.earliestDate, core.latestDate, core.startDate, core.endDate, core.performanceStartDate, core.performanceEndDate])

  return useMemo(() => ({
    ...core,
    modelId,
    ...trade,
    ...graphState,
    ...effectiveDates,
    isGraphInitialized,
    ...graphUpdaters,
  }), [core, modelId, trade, graphState, effectiveDates, isGraphInitialized, graphUpdaters])
}
